import { useQuery } from '@tanstack/react-query';
import { MenuItem, Stack, TextField } from '@mui/material';
import { useUniversityOptions } from '../../../hooks/useUniversityOptions';
import { listLookup, LookupRecord } from '../../../api/masterData';
import { WizardValues } from './wizardTypes';
import { SearchableLookupField } from './SearchableLookupField';
import { isoToFlagEmoji } from './wizardUtils';

interface Step3Props {
  values: WizardValues;
  setField: <K extends keyof WizardValues>(field: K, value: WizardValues[K]) => void;
}

export const Step3StudyPreferences = ({ values, setField }: Step3Props) => {
  const qualificationLevelsQuery = useQuery({
    queryKey: ['lookup', '/master-data/academic-levels'],
    queryFn: () => listLookup('/master-data/academic-levels', { activeOnly: true }),
  });
  const studyAreasQuery = useQuery({
    queryKey: ['lookup', '/master-data/study-areas'],
    queryFn: () => listLookup('/master-data/study-areas', { activeOnly: true }),
  });
  const countriesQuery = useQuery({
    queryKey: ['lookup', '/master-data/countries'],
    queryFn: () => listLookup('/master-data/countries', { activeOnly: true }),
  });
  // Active universities, plus this lead's own saved one if deactivated.
  const universitiesQuery = useUniversityOptions(
    [values.preferredUniversityId],
    values.countryId ? { countryId: values.countryId } : {},
  );
  const flagFor = (option: LookupRecord) =>
    typeof option.iso2 === 'string' ? isoToFlagEmoji(option.iso2) : undefined;

  return (
    <Stack spacing={2}>
      <TextField
        select
        label="Preferred qualification"
        fullWidth
        value={values.preferredQualificationLevelId}
        onChange={(e) => setField('preferredQualificationLevelId', e.target.value ? Number(e.target.value) : '')}
      >
        <MenuItem value="">
          <em>Not specified</em>
        </MenuItem>
        {qualificationLevelsQuery.data?.map((l) => (
          <MenuItem key={l.id as number} value={l.id as number}>
            {String(l.name)}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Preferred study area"
        fullWidth
        value={values.preferredStudyAreaId}
        onChange={(e) => setField('preferredStudyAreaId', e.target.value ? Number(e.target.value) : '')}
      >
        <MenuItem value="">
          <em>Not specified</em>
        </MenuItem>
        {studyAreasQuery.data?.map((a) => (
          <MenuItem key={a.id as number} value={a.id as number}>
            {String(a.name)}
          </MenuItem>
        ))}
      </TextField>

      <SearchableLookupField
        label="Preferred country"
        options={countriesQuery.data}
        value={values.countryId}
        onChange={(id) => setField('countryId', id)}
        getFlag={flagFor}
      />

      <SearchableLookupField
        label="Preferred university"
        options={universitiesQuery.data}
        value={values.preferredUniversityId}
        onChange={(id) => setField('preferredUniversityId', id)}
      />

      <TextField
        label="Preferred intake"
        placeholder="e.g. January 2027"
        fullWidth
        value={values.preferredIntakeLabel}
        onChange={(e) => setField('preferredIntakeLabel', e.target.value)}
      />
    </Stack>
  );
};
